import { getHourAndMinutes } from "../../../main/themes/defaultLayout";

export function getChartHourLabel(dateTime?: string): string {
  if (!dateTime) {
    return "";
  }

  const isoTimeMatch = dateTime.match(/T(\d{2}:\d{2})/);
  if (isoTimeMatch?.[1]) {
    return isoTimeMatch[1];
  }

  return getHourAndMinutes(dateTime);
}

/**
 * Chave de alinhamento por minuto (ignora segundos/ms e timezone suffix).
 * Potência e irradiância raramente compartilham o mesmo segundo exato.
 */
export function toMinuteAlignmentKey(dateTime?: string): string {
  if (!dateTime) {
    return "";
  }

  const isoMatch = dateTime.match(/^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2})/);
  if (isoMatch?.[1]) {
    return isoMatch[1];
  }

  const parsed = Date.parse(dateTime);
  if (!Number.isNaN(parsed)) {
    const d = new Date(parsed);
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    const hh = String(d.getHours()).padStart(2, "0");
    const min = String(d.getMinutes()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}T${hh}:${min}`;
  }

  return dateTime;
}

/**
 * Aligns `values` onto the `reference` timeline by minute.
 * Missing minutes become `null` (chart gap) — never invent `0`.
 * When multiple values share a minute, prefers a non-zero average.
 */
export function alignSeriesValuesByDateTime<
  TRef extends { dateTime?: string },
  TVal extends { dateTime?: string; value?: number },
>(reference?: TRef[], values?: TVal[]): (number | null)[] {
  if (!reference?.length) {
    return [];
  }

  const buckets = new Map<string, number[]>();
  for (const item of values ?? []) {
    const key = toMinuteAlignmentKey(item.dateTime);
    if (!key || typeof item.value !== "number") {
      continue;
    }
    const list = buckets.get(key);
    if (list) {
      list.push(item.value);
    } else {
      buckets.set(key, [item.value]);
    }
  }

  const valueByMinute = new Map<string, number>();
  for (const [key, nums] of buckets) {
    const nonZero = nums.filter((v) => v !== 0);
    const source = nonZero.length > 0 ? nonZero : nums;
    valueByMinute.set(
      key,
      source.reduce((sum, v) => sum + v, 0) / source.length,
    );
  }

  return reference.map((ref) => {
    const key = toMinuteAlignmentKey(ref.dateTime);
    if (!key || !valueByMinute.has(key)) {
      return null;
    }
    return valueByMinute.get(key) ?? null;
  });
}

/** ECharts: `null` pode ser desenhado como 0; `'-'` abre gap real na linha. */
export function toEchartsLineData(
  values: (number | null)[],
): (number | "-")[] {
  return values.map((v) => (v == null ? "-" : v));
}

export function mapSeriesXLabels<
  T extends { dateTime?: string },
>(items?: T[]): string[] {
  return items?.map((item) => getChartHourLabel(item.dateTime)) ?? [];
}

export function mapSeriesValues<
  T extends { value?: number },
>(items?: T[]): number[] {
  return items?.map((item) => item.value ?? 0) ?? [];
}

export function formatIrradianceTooltipValue(
  value: unknown,
  unit: string,
): string {
  if (value == null || typeof value !== "number") {
    return `sem dado`;
  }
  const vPtBr = value.toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `${vPtBr} ${unit}`;
}

/**
 * Timeline contínuo por minuto para o gráfico de potência × irradiância.
 * Começa às 06:00 do dia das séries até o último minuto disponível —
 * assim o eixo não “pula” para o primeiro ponto de potência (~10:48)
 * quando a irradiância já existe desde o início da manhã.
 */
export function buildPotenciaChartMinuteTimeline<
  T extends { dateTime?: string },
>(
  ...seriesList: (T[] | undefined)[]
): { dateTime: string }[] {
  const minuteKeys = new Set<string>();
  for (const series of seriesList) {
    for (const item of series ?? []) {
      const key = toMinuteAlignmentKey(item.dateTime);
      if (key) {
        minuteKeys.add(key);
      }
    }
  }

  if (minuteKeys.size === 0) {
    return [];
  }

  const sorted = [...minuteKeys].sort();
  const lastKey = sorted[sorted.length - 1];
  const day = lastKey.slice(0, 10); // YYYY-MM-DD
  const startKey = `${day}T06:00`;

  const parseKey = (key: string): number => {
    const [datePart, timePart] = key.split("T");
    const [y, m, d] = datePart.split("-").map(Number);
    const [hh, mm] = timePart.split(":").map(Number);
    return Date.UTC(y, m - 1, d, hh, mm, 0, 0);
  };

  const startMs = parseKey(startKey);
  const endMs = parseKey(lastKey);
  if (endMs < startMs) {
    return sorted.map((dateTime) => ({ dateTime: `${dateTime}:00` }));
  }

  const timeline: { dateTime: string }[] = [];
  for (let cursor = startMs; cursor <= endMs; cursor += 60_000) {
    const d = new Date(cursor);
    const yyyy = d.getUTCFullYear();
    const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
    const dd = String(d.getUTCDate()).padStart(2, "0");
    const hh = String(d.getUTCHours()).padStart(2, "0");
    const min = String(d.getUTCMinutes()).padStart(2, "0");
    timeline.push({ dateTime: `${yyyy}-${mm}-${dd}T${hh}:${min}:00` });
  }

  return timeline;
}

export function getInitialSliderRange(
  length: number,
  startWithFullRange?: boolean,
): [number, number] {
  if (length === 0) {
    return [1, 0];
  }

  const max = length;
  const min = startWithFullRange ? 1 : Math.max(1, max - 30);
  return [min, max];
}
